// Intestazione (redesign 2026-10, mockup "Home B2B goooled").
// ☰ menu · logo · ricerca · Admin · lingua · carrello · menu utente (iniziali).
// Prezzi visibili/nascosti: si gestisce dal Profilo (AccountDrawer).
import React, { useContext, useEffect, useRef, useState } from 'react';
import { FiMenu, FiSearch, FiX, FiShield, FiShoppingCart, FiChevronRight, FiLogOut } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import AccountDrawer from '../components/AccountDrawer';
import { CartContext } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import '../css/Shell.css';

const renderFlag = (code) => {
  if (code === 'it') {
    return (
      <svg className="flag-svg" viewBox="0 0 24 16" aria-hidden="true" focusable="false">
        <rect x="0" y="0" width="8" height="16" fill="#009246" />
        <rect x="8" y="0" width="8" height="16" fill="#FFFFFF" />
        <rect x="16" y="0" width="8" height="16" fill="#CE2B37" />
      </svg>
    );
  }
  if (code === 'en') {
    return (
      <svg className="flag-svg" viewBox="0 0 24 16" aria-hidden="true" focusable="false">
        <rect width="24" height="16" fill="#012169" />
        <polygon points="0,0 2.5,0 24,13.5 24,16 21.5,16 0,2.5" fill="#FFFFFF" />
        <polygon points="24,0 21.5,0 0,13.5 0,16 2.5,16 24,2.5" fill="#FFFFFF" />
        <polygon points="0,0 1.2,0 24,14.8 24,16 22.8,16 0,1.2" fill="#C8102E" />
        <polygon points="24,0 22.8,0 0,14.8 0,16 1.2,16 24,1.2" fill="#C8102E" />
        <rect x="9.5" y="0" width="5" height="16" fill="#FFFFFF" />
        <rect x="0" y="5.5" width="24" height="5" fill="#FFFFFF" />
        <rect x="10.5" y="0" width="3" height="16" fill="#C8102E" />
        <rect x="0" y="6.5" width="24" height="3" fill="#C8102E" />
      </svg>
    );
  }
  if (code === 'es') {
    return (
      <svg className="flag-svg" viewBox="0 0 24 16" aria-hidden="true" focusable="false">
        <rect x="0" y="0" width="24" height="4" fill="#AA151B" />
        <rect x="0" y="4" width="24" height="8" fill="#F1BF00" />
        <rect x="0" y="12" width="24" height="4" fill="#AA151B" />
      </svg>
    );
  }
  if (code === 'fr') {
    return (
      <svg className="flag-svg" viewBox="0 0 24 16" aria-hidden="true" focusable="false">
        <rect x="0" y="0" width="8" height="16" fill="#0055A4" />
        <rect x="8" y="0" width="8" height="16" fill="#FFFFFF" />
        <rect x="16" y="0" width="8" height="16" fill="#EF4135" />
      </svg>
    );
  }
  return (
    <svg className="flag-svg" viewBox="0 0 24 16" aria-hidden="true" focusable="false">
      <rect x="0" y="0" width="24" height="5.333" fill="#000000" />
      <rect x="0" y="5.333" width="24" height="5.334" fill="#DD0000" />
      <rect x="0" y="10.667" width="24" height="5.333" fill="#FFCE00" />
    </svg>
  );
};

const initialsOf = (user) => {
  const src = String(user?.name || user?.email || '').trim();
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

// Chiude un menu a comparsa con un clic fuori o con Esc
const useDismiss = (open, ref, onClose) => {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, ref, onClose]);
};

const Header = ({ onSearch, user, onLogout, togglePrices, showPrices, onToggleSidebar, isSidebarOpen, isMobile }) => {
  const navigate = useNavigate();
  const { lang, setLang, t } = useLanguage();
  const { cart } = useContext(CartContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [account, setAccount] = useState({ open: false, tab: 'profile' });
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);

  useDismiss(langMenuOpen, langMenuRef, () => setLangMenuOpen(false));
  useDismiss(userMenuOpen, userMenuRef, () => setUserMenuOpen(false));

  // Ctrl+K / ⌘K porta alla ricerca
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchRef.current?.focus(), 0);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    onSearch(e.target.value);
  };

  const confirmLogout = () => {
    setUserMenuOpen(false);
    if (window.confirm(t('header.logoutConfirm'))) {
      onLogout();
    }
  };

  const openAccount = (tab) => {
    setUserMenuOpen(false);
    setAccount({ open: true, tab });
  };

  const languageOptions = [
    { code: 'it', label: t('header.languageIt') },
    { code: 'en', label: t('header.languageEn') },
    { code: 'de', label: t('header.languageDe') },
    { code: 'es', label: t('header.languageEs') },
    { code: 'fr', label: t('header.languageFr') },
  ];
  const cartCount = cart.length;
  const showSearchInput = !isMobile || searchOpen;

  return (
    <header className={`hdr${isMobile ? ' hdr-mobile' : ''}${isMobile && searchOpen ? ' hdr-searching' : ''}`}>
      <button
        type="button"
        className="hdr-icon"
        onClick={onToggleSidebar}
        aria-label={isSidebarOpen ? t('header.closeMenu') : t('header.openMenu')}
        title={isSidebarOpen ? t('header.closeMenu') : t('header.openMenu')}
      >
        <FiMenu />
      </button>

      <button type="button" className="hdr-brand" onClick={() => navigate('/dashboard')} aria-label="Home">
        <img src="/images/logo.png" alt="Goooled" />
        <span className="hdr-tag">B2B</span>
      </button>

      {showSearchInput && (
        <label className="hdr-search">
          <FiSearch aria-hidden="true" />
          <input
            ref={searchRef}
            type="text"
            placeholder={t('header.searchPlaceholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label={t('header.searchField')}
          />
          {!isMobile && <span className="hdr-kbd">Ctrl K</span>}
          {isMobile && (
            <button type="button" className="hdr-icon" onClick={() => setSearchOpen(false)} aria-label={t('header.closeSearch')}>
              <FiX />
            </button>
          )}
        </label>
      )}

      <div className="hdr-right">
        {isMobile && !searchOpen && (
          <button type="button" className="hdr-icon" onClick={() => setSearchOpen(true)} aria-label={t('header.openSearch')}>
            <FiSearch />
          </button>
        )}

        {user && user.role === 'admin' && (
          <button type="button" className="hdr-chip hdr-hide-mobile" onClick={() => navigate('/admin-dashboard')}>
            <FiShield aria-hidden="true" />
            <span>{t('header.admin')}</span>
          </button>
        )}

        <div className="hdr-pop-wrap hdr-hide-mobile" ref={langMenuRef}>
          <button
            type="button"
            className="hdr-icon"
            onClick={() => setLangMenuOpen((prev) => !prev)}
            aria-label={t('header.language')}
            title={t('header.language')}
            aria-expanded={langMenuOpen}
          >
            {renderFlag(lang)}
          </button>
          {langMenuOpen && (
            <div className="hdr-pop hdr-pop-lang" role="menu">
              {languageOptions.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  className={`hdr-pop-item${lang === option.code ? ' is-active' : ''}`}
                  onClick={() => {
                    setLang(option.code);
                    setLangMenuOpen(false);
                  }}
                >
                  {renderFlag(option.code)}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button type="button" className="hdr-icon" onClick={() => navigate('/cart')} aria-label={t('sidebar.cart')} title={t('sidebar.cart')}>
          <FiShoppingCart />
          {cartCount > 0 && <span className="hdr-badge">{cartCount}</span>}
        </button>

        <div className="hdr-pop-wrap" ref={userMenuRef}>
          <button
            type="button"
            className="hdr-avatar"
            onClick={() => setUserMenuOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            title={t('account.title')}
          >
            {initialsOf(user)}
          </button>
          {userMenuOpen && (
            <div className="hdr-pop hdr-pop-user" role="menu">
              <div className="hdr-who">
                <strong>{user?.name}</strong>
                <span>{user?.email}</span>
                {user?.companyName && <span>{user.companyName}</span>}
              </div>
              <button type="button" className="hdr-pop-item" onClick={() => openAccount('profile')}>
                <span>{t('header.menuProfile')}</span><FiChevronRight aria-hidden="true" />
              </button>
              <button type="button" className="hdr-pop-item" onClick={() => openAccount('book')}>
                <span>{t('header.menuAddressBook')}</span><FiChevronRight aria-hidden="true" />
              </button>
              <button type="button" className="hdr-pop-item" onClick={() => openAccount('feedback')}>
                <span>{t('header.menuFeedback')}</span><FiChevronRight aria-hidden="true" />
              </button>
              {isMobile && user && user.role === 'admin' && (
                <button type="button" className="hdr-pop-item" onClick={() => { setUserMenuOpen(false); navigate('/admin-dashboard'); }}>
                  <span>{t('header.admin')}</span><FiShield aria-hidden="true" />
                </button>
              )}
              <div className="hdr-pop-sep" />
              <div className="hdr-langs" role="group" aria-label={t('header.language')}>
                {languageOptions.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    className={`hdr-lang${lang === option.code ? ' is-active' : ''}`}
                    onClick={() => setLang(option.code)}
                    title={option.label}
                    aria-label={option.label}
                    aria-pressed={lang === option.code}
                  >
                    {renderFlag(option.code)}
                  </button>
                ))}
              </div>
              <div className="hdr-pop-sep" />
              <button type="button" className="hdr-pop-item is-danger" onClick={confirmLogout}>
                <span>{t('header.logout')}</span><FiLogOut aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>

      <AccountDrawer
        open={account.open}
        initialTab={account.tab}
        onClose={() => setAccount((prev) => ({ ...prev, open: false }))}
        togglePrices={togglePrices}
        showPrices={showPrices}
      />
    </header>
  );
};

export default Header;
